// The hangar bay: the unit in the carousel's spotlight, on a slow turntable
// at the top of its card. A stat bar tells you a Spitfire turns well; the bay
// shows you the Spitfire — and, while you sweep the PAINT SHOP, the Spitfire
// in YQ-P's codes, so a locked livery is a picture of what the kills buy
// instead of a padlock on a coloured chip.
//
// It draws the game's own models (PlaneVisual for an airframe, the unit's
// ModelInstance for everything else), painted by the same code that paints
// them in the war, so the bay can never show a plane you won't get. A model
// still loading (or ?boxes, or a portal that can't reach the files) shows the
// box silhouette, exactly as the war would.
//
// It renders through a renderer of its own on a small canvas — the game's
// canvas sits UNDER the lobby, so the bay can't borrow it. Geometry and
// materials are shared with the main renderer (three keys its GPU state per
// renderer), and the bay never disposes a shared resource. It only spins while
// its canvas is on screen; a software rasterizer (SwiftShader, llvmpipe) or a
// pilot who asked for reduced motion gets one still frame per change instead.

import * as THREE from "three";
import { loadModel, ModelInstance, modelsEnabled } from "./models.js";
import { PlaneVisual, CLASS_MODELS, schemeFits } from "./planes.js";
import { modelForClass } from "./aircraft_models.js";
import {
  AIR_VEHICLE_MODEL,
  EMPLACEMENTS,
  LAND_VEHICLE_MODEL,
  VEHICLE_MODELS,
  navalModelOf,
} from "./unit_models.js";
import { softwareRenderer } from "./gfx.js";

// The turntable: one lap in ~18 s — slow enough to read the markings as they
// pass, quick enough that you see the other side before you've moved on.
const SPIN_RAD_PER_SEC = 0.35;
// Where the turntable stops for a still frame (and starts spinning from): the
// three-quarter front a builder's photo is taken from, nose to the right.
const REST_YAW = -0.6;
// The camera looks down on the model a little, like a crew walking up to it.
const ELEVATION = THREE.MathUtils.degToRad(17);
const FOV = 28;

/** What a card's `preview` descriptor shows: `{ plane }` an airframe,
 * `{ hull }` a roster hull (config.groundVehicles.types index), `{ gun }` an
 * emplacement (GUN_STEMS), `{ stem }` a model by name. Returns { plane } or
 * { stem }, or null for nothing. */
export function resolvePreview(desc, hullTypes = []) {
  if (!desc) return null;
  if (desc.plane != null) return { plane: desc.plane | 0 };
  if (desc.stem) return { stem: desc.stem };
  if (desc.gun) return stemOr(GUN_STEMS[desc.gun]);
  if (desc.hull != null) {
    const i = desc.hull | 0;
    const t = hullTypes[i];
    if (t?.naval) return stemOr(navalModelOf(t, i)?.stem);
    if (t?.land) {
      // The land art table is indexed within its class: a mixed roster (the
      // armada) lists its warships first.
      const base = Math.max(0, hullTypes.findIndex((h) => h?.land));
      return stemOr(VEHICLE_MODELS[LAND_VEHICLE_MODEL[i - base]]?.stem);
    }
    return stemOr(VEHICLE_MODELS[AIR_VEHICLE_MODEL[i]]?.stem);
  }
  return null;
}
const stemOr = (stem) => (stem ? { stem } : null);

/** The emplacement stems the lobby's gun cards show. */
export const GUN_STEMS = {
  flak: EMPLACEMENTS.flak88.stem,
  navy: EMPLACEMENTS.bofors40.stem,
  // The Battleship Turret card crews Big J's main battery: show her whole.
  bigGuns: "iowa_realscale",
};

/** The level-0 meshes of `root` with each one's matrix into root's frame:
 * an LOD counts only its nearest level (the far ones are the same unit,
 * coarser), and a hidden subtree not at all (the nameplate, an unlit muzzle
 * flash). */
function previewMeshes(root) {
  const out = [];
  root.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const visit = (o) => {
    if (!o.visible) return;
    if (o.isMesh && o.geometry?.attributes?.position) {
      out.push([o.geometry, new THREE.Matrix4().multiplyMatrices(inv, o.matrixWorld)]);
    }
    if (o.isLOD && o.levels?.length) {
      visit(o.levels[0].object);
      return;
    }
    for (const c of o.children) visit(c);
  };
  visit(root);
  return out;
}

/** The bounding box of `root`'s level-0 meshes, in root's own frame. */
export function previewBounds(root) {
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  for (const [geo, m] of previewMeshes(root)) {
    if (!geo.boundingBox) geo.computeBoundingBox();
    box.union(tmp.copy(geo.boundingBox).applyMatrix4(m));
  }
  return box;
}

/** The points of `root`'s silhouette that can touch the frame's edge as it
 * turns about the vertical through `center`: per slice of bearing, the
 * vertex farthest out, the highest and the lowest. A few hundred points
 * stand in for the whole mesh — and, unlike the bounding box, they know a
 * Spitfire is a cross, not a crate. */
export function previewOutline(root, center, bins = 64) {
  const far = new Array(bins).fill(null);
  const hi = new Array(bins).fill(null);
  const lo = new Array(bins).fill(null);
  const v = new THREE.Vector3();
  for (const [geo, m] of previewMeshes(root)) {
    const pos = geo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m).sub(center);
      const k = Math.min(bins - 1, Math.floor(((Math.atan2(v.z, v.x) + Math.PI) / (2 * Math.PI)) * bins));
      const r = v.x * v.x + v.z * v.z;
      if (!far[k] || r > far[k].r) far[k] = { r, p: v.clone() };
      if (!hi[k] || v.y > hi[k].p.y) hi[k] = { p: v.clone() };
      if (!lo[k] || v.y < lo[k].p.y) lo[k] = { p: v.clone() };
    }
  }
  return [...far, ...hi, ...lo].filter(Boolean).map((e) => e.p);
}

// How much of the frame the turntable may fill (NDC half-extent): a little
// air at the edges, and the livery caption's corner mostly clear.
const FILL = 0.9;
const YAW_SAMPLES = 32;

/** How far back the camera stands so the model whose outline is `points`
 * (centred on the turntable's axis) stays inside a `vfov`° x `aspect` frame
 * at every yaw. Solved, not estimated: a long hull swinging broadside comes
 * nearer the lens at one end than the other, and perspective makes that end
 * the one that leaves the frame — the Iowa's stern did, under a
 * bounding-disc guess. */
export function fitDistance(points, aspect, vfov = FOV, elev = ELEVATION, fill = FILL) {
  const cam = new THREE.PerspectiveCamera(vfov, aspect, 0.01, 1e7);
  const rot = new THREE.Matrix4();
  const v = new THREE.Vector3();
  const fits = (d) => {
    cam.position.set(0, d * Math.sin(elev), d * Math.cos(elev));
    cam.lookAt(0, 0, 0);
    cam.updateMatrixWorld();
    for (let k = 0; k < YAW_SAMPLES; k++) {
      rot.makeRotationY((k / YAW_SAMPLES) * Math.PI * 2);
      for (const c of points) {
        v.copy(c).applyMatrix4(rot).applyMatrix4(cam.matrixWorldInverse);
        if (v.z > -0.01) return false; // the lens is inside the model
        v.applyMatrix4(cam.projectionMatrix);
        if (Math.abs(v.x) > fill || Math.abs(v.y) > fill) return false;
      }
    }
    return true;
  };
  let size = 1;
  for (const p of points) size = Math.max(size, p.length());
  let lo = 0;
  let hi = size;
  while (!fits(hi)) {
    lo = hi;
    hi *= 2;
  }
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) hi = mid;
    else lo = mid;
  }
  return hi;
}

export class HangarBay {
  /** `hullTypes` is config.groundVehicles.types; `team()` the side the
   * lobby is for (Crimson until the server seats us). */
  constructor({ hullTypes = [], team = () => 0 } = {}) {
    this.hullTypes = hullTypes;
    this.team = team;
    this.canvas = document.createElement("canvas");
    this.canvas.className = "bay-canvas";
    this.canvas.setAttribute("aria-hidden", "true");
    this.renderer = null;
    this.failed = false;
    this.scene = new THREE.Scene();
    // Daylight over the Brick Sea: the war's own sun and sky (world.js),
    // with a cool rim behind so a dark airframe keeps its outline.
    const sun = new THREE.DirectionalLight(0xfff3da, 2.4);
    sun.position.set(-0.6, 1, 0.8);
    const rim = new THREE.DirectionalLight(0xbcdcff, 0.9);
    rim.position.set(0.5, 0.4, -1);
    this.scene.add(sun, rim, new THREE.HemisphereLight(0xbcdcff, 0x49603e, 1.1));
    this.camera = new THREE.PerspectiveCamera(FOV, 2, 0.1, 5000);
    this.pivot = new THREE.Group();
    this.scene.add(this.pivot);
    this.key = "";      // what the turntable holds ("p3", "s:flak88")
    this.scheme = 0;    // the paint it wears (airframes)
    this.visual = null; // a PlaneVisual
    this.inst = null;   // a ModelInstance
    this.token = null;  // the load in flight
    this.dirty = true;  // a still frame owes a redraw
    this.running = false;
    this.last = 0;
    this.still =
      softwareRenderer() ||
      (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);
    this.onState = null; // (state: "loading" | "ready" | "none") → the bay's frame
  }

  /** Put the canvas in `host` and show `desc` there (a card's preview
   * descriptor) in paint scheme `scheme` (airframes only). */
  mount(host, desc, scheme = 0) {
    if (!host) return;
    if (this.canvas.parentElement !== host) host.appendChild(this.canvas);
    this.show(desc, scheme);
  }

  /** Change what's on the turntable. Same unit, new paint: a repaint, not a
   * reload. */
  show(desc, scheme = 0) {
    const want = resolvePreview(desc, this.hullTypes);
    const key = !want ? "" : want.plane != null ? `p${want.plane}` : `s:${want.stem}`;
    if (key && !this._ensureRenderer()) return this._state("none");
    if (key !== this.key) {
      this._clear();
      this.key = key;
      this.scheme = this._fit(want, scheme);
      if (!want) this._state("none");
      else this._load(want);
    } else {
      this.setScheme(scheme);
    }
    this.wake();
  }

  /** Stop the turntable at `yaw` (radians about the vertical; the default is
   * the builder's three-quarter) and hold it there: the screenshot
   * director's shutter. */
  hold(yaw = REST_YAW) {
    this.still = true;
    this.restYaw = yaw;
    this.pivot.rotation.y = yaw;
    this.dirty = true;
    this.wake();
  }

  /** Repaint the airframe on the turntable (a PAINT SHOP swatch under the
   * pointer). A livery painted for another airframe shows the factory
   * finish, as it would fly. */
  setScheme(scheme) {
    if (!this.key.startsWith("p")) return;
    const s = this._fit({ plane: Number(this.key.slice(1)) }, scheme);
    if (s === this.scheme) return;
    this.scheme = s;
    this.visual?.setScheme(s);
    this.dirty = true;
    this.wake();
  }

  /** Does paint scheme `i` show on airframe `plane`? (The factory finish
   * always does; a livery only on its own airframe.) */
  fits(plane, i) {
    if (!i) return true;
    const def = this._schemes?.[i];
    return !!def && schemeFits(def, plane);
  }

  // The scheme the turntable actually paints: the asked-for one if it fits
  // the airframe, else the factory finish (and none at all on a hull).
  _fit(want, scheme) {
    return want?.plane != null && this.fits(want.plane, scheme | 0) ? scheme | 0 : 0;
  }

  /** The scheme table (config.schemes), for the fit check above. */
  setSchemes(list) {
    this._schemes = Array.isArray(list) ? list : [];
  }

  _load(want) {
    const token = (this.token = {});
    this._state("loading");
    const team = this.team() | 0;
    if (want.plane != null) {
      // Fetch the airframe's levels first so the model mounts on the first
      // frame — no box silhouette flashing up and being swapped out.
      const spec = modelForClass(want.plane, CLASS_MODELS);
      const stems = spec ? [spec.stem, `${spec.stem}_lod1`, `${spec.stem}_lod2`] : [];
      Promise.allSettled(stems.map((s) => loadModel(s))).then(() => {
        if (token !== this.token) return;
        const v = new PlaneVisual(this.pivot, team, "", false, want.plane, this.scheme);
        if (v.nameplate) v.nameplate.visible = false; // the card already names it
        v.throttle = 0.15; // props ticking over on the hardstand
        this.visual = v;
        this._frame(v.group);
      });
      return;
    }
    if (!modelsEnabled()) return this._state("none");
    loadModel(want.stem).then(
      (tpl) => {
        if (token !== this.token) return;
        this.inst = new ModelInstance(tpl, { team });
        this.pivot.add(this.inst.root);
        this._frame(this.inst.root);
      },
      () => token === this.token && this._state("none"),
    );
  }

  // Centre the model on the turntable and stand the camera off to fit it.
  _frame(obj) {
    this.pivot.rotation.y = 0;
    obj.position.set(0, 0, 0);
    const b = previewBounds(obj);
    if (b.isEmpty()) return this._state("none");
    const c = b.getCenter(new THREE.Vector3());
    this.outline = previewOutline(obj, c);
    obj.position.set(-c.x, -c.y, -c.z);
    this.box = b.translate(c.negate());
    this._fitAspect = 0; // re-solve the stand-off for this model
    this.pivot.rotation.y = this.restYaw ?? REST_YAW;
    this._aim();
    this.dirty = true;
    this._state("ready");
    this.wake();
  }

  _aim() {
    if (!this.box) return;
    if (this._fitAspect !== this.camera.aspect) {
      this._fitAspect = this.camera.aspect;
      this._dist = fitDistance(this.outline, this.camera.aspect);
    }
    const d = this._dist;
    const r = this.box.getSize(new THREE.Vector3()).length() / 2;
    this.camera.position.set(0, d * Math.sin(ELEVATION), d * Math.cos(ELEVATION));
    this.camera.near = Math.max(0.05, d - r * 1.2);
    this.camera.far = d + r * 1.2;
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
  }

  _clear() {
    this.token = null;
    if (this.visual) this.visual.dispose(this.pivot);
    if (this.inst) this.pivot.remove(this.inst.root);
    this.visual = null;
    this.inst = null;
    this.box = null;
  }

  _state(s) {
    this.state = s;
    this.onState?.(s);
  }

  _ensureRenderer() {
    if (this.renderer) return true;
    if (this.failed) return false;
    try {
      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: true,
        alpha: true,
        powerPreference: "low-power",
      });
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 2));
      this.canvas.addEventListener("webglcontextlost", (e) => {
        e.preventDefault();
        this.dirty = true;
      });
      this.canvas.addEventListener("webglcontextrestored", () => this.wake());
      return true;
    } catch {
      // No second context on this device: the cards read as they always did.
      this.failed = true;
      return false;
    }
  }

  // On screen: in the document, laid out, and the tab in front.
  _onScreen() {
    return (
      this.canvas.isConnected &&
      this.canvas.offsetParent !== null &&
      !(typeof document !== "undefined" && document.hidden)
    );
  }

  /** Start the turntable if it has something to show and somewhere to show
   * it; it parks itself as soon as either goes away. */
  wake() {
    if (this.running || !this.renderer || !this.key) return;
    this.running = true;
    this.last = performance.now();
    requestAnimationFrame((t) => this._tick(t));
  }

  _tick(t) {
    if (!this._onScreen() || !this.key) {
      this.running = false;
      this.dirty = true; // owe a frame when it comes back
      return;
    }
    const dt = Math.min(0.1, Math.max(0, (t - this.last) / 1000));
    this.last = t;
    const w = this.canvas.clientWidth | 0;
    const h = this.canvas.clientHeight | 0;
    if (w > 0 && h > 0 && (w !== this._w || h !== this._h)) {
      this._w = w;
      this._h = h;
      this.renderer.setSize(w, h, false);
      this.camera.aspect = w / h;
      this._aim();
      this.dirty = true;
    }
    const obj = this.visual?.group ?? this.inst?.root;
    if (obj && this.state === "ready") {
      if (!this.still) {
        this.pivot.rotation.y -= dt * SPIN_RAD_PER_SEC;
        this.visual?.update(dt, false);
        this.dirty = true;
      }
      if (this.dirty && w > 0 && h > 0) {
        this.renderer.render(this.scene, this.camera);
        this.dirty = false;
      }
    }
    if (this.still && !this.dirty && this.state === "ready") {
      // Nothing moves: park until the next show/repaint wakes us.
      this.running = false;
      return;
    }
    requestAnimationFrame((tt) => this._tick(tt));
  }
}
